import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AssistantPanel } from "./AssistantPanel";
import "./assistant.css";

type AssistantApi = {
  /** Opens the panel; with a question, asks it. */
  open: (question?: string) => void;
  close: () => void;
  isOpen: boolean;
};

const Ctx = createContext<AssistantApi | null>(null);

/** AbiChat, for any page of the app: `useAssistant()?.open("Can I retire at 60?")`. */
export function useAssistant(): AssistantApi | null {
  return useContext(Ctx);
}

/**
 * AbiChat's panel lives in the app shell, beside the page and outside it, so it stays open (and
 * keeps its conversation) while the user moves around the app, including where Abi takes them.
 */
export function AssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [ask, setAsk] = useState<{ q: string; n: number } | undefined>();
  const open = useCallback((question?: string) => {
    setOpen(true);
    const q = question?.trim();
    if (q) setAsk((a) => ({ q, n: (a?.n ?? 0) + 1 }));
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const api = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);
  // Wide screens move the page over (assistant.css `#app.ac-docked`); the shell keeps its own
  // structure (its styles select `#app > main`), so nothing wraps it.
  useEffect(() => {
    document.getElementById("app")?.classList.toggle("ac-docked", isOpen);
  }, [isOpen]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {isOpen ? (
        <AssistantPanel onClose={close} ask={ask} />
      ) : (
        <button
          type="button"
          className="ac-launch"
          onClick={() => open()}
          aria-label="Ask Abi"
          data-testid="assistant-launch"
        >
          <span className="mk" aria-hidden="true" />
          Ask Abi
        </button>
      )}
    </Ctx.Provider>
  );
}
